import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../../services/api';
import CompanyChecklist from './CompanyChecklist';
import NewUserForm from './NewUserForm';

const fullName = u => [u.username, u.userlastname].filter(Boolean).join(' ') || u.usermail;

/** Clientes de un abogado, siempre dentro de los de su despacho. */
function LawyerRow({ lawyer, partnerCompanies, onSaved }) {
  const [selected, setSelected] = useState((lawyer.company_ids || []).map(String));
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelected((lawyer.company_ids || []).map(String)), [lawyer.company_ids]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/partners/lawyers/${lawyer._id}/companies`, { company_ids: selected });
      toast.success('Clientes del abogado guardados');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className='border border-slate-200 rounded-lg p-3 space-y-2'>
      <div className='text-sm'>
        <span className='font-medium text-slate-800'>{fullName(lawyer)}</span>
        <span className='ml-2 text-slate-500'>{lawyer.usermail}</span>
      </div>
      <CompanyChecklist companies={partnerCompanies} selected={selected} onChange={setSelected} disabled={saving} />
      <div className='flex justify-end'>
        <button
          type='button'
          onClick={save}
          disabled={saving}
          className='px-3 py-1 text-xs rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50'
        >
          Guardar clientes
        </button>
      </div>
    </li>
  );
}

/**
 * Un despacho: sus clientes y sus abogados. Quitarle un cliente al despacho se
 * lo quita también a sus abogados (lo hace el backend).
 */
function PartnerCard({ partner, companies, onChanged }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState((partner.company_ids || []).map(String));
  const [lawyers, setLawyers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelected((partner.company_ids || []).map(String)), [partner.company_ids]);

  const loadLawyers = () =>
    api
      .get(`/partners/${partner._id}/lawyers`)
      .then(({ data }) => setLawyers(data.data || []))
      .catch(err => toast.error(getErrorMessage(err)));

  useEffect(() => {
    if (open) loadLawyers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partner._id]);

  const partnerCompanies = companies.filter(c => (partner.company_ids || []).map(String).includes(c._id));

  const saveCompanies = async () => {
    setSaving(true);
    try {
      await api.put(`/partners/${partner._id}/companies`, { company_ids: selected });
      toast.success('Clientes del despacho guardados');
      onChanged();
      loadLawyers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const createLawyer = async body => {
    try {
      await api.post(`/partners/${partner._id}/lawyers`, body);
      toast.success('Abogado creado');
      setAdding(false);
      loadLawyers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <li className='border border-slate-200 rounded-lg'>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 rounded-lg'
      >
        <div>
          <div className='text-sm font-semibold text-slate-800'>{fullName(partner)}</div>
          <div className='text-xs text-slate-500'>
            {partner.usermail} · {(partner.company_ids || []).length} clientes
          </div>
        </div>
        <span className='text-xs text-blue-600'>{open ? 'Cerrar' : 'Gestionar'}</span>
      </button>

      {open && (
        <div className='px-4 pb-4 space-y-4'>
          <div>
            <h3 className='text-xs font-semibold uppercase text-slate-500 mb-2'>Clientes del despacho</h3>
            <CompanyChecklist companies={companies} selected={selected} onChange={setSelected} disabled={saving} />
            <div className='flex justify-end mt-2'>
              <button
                type='button'
                onClick={saveCompanies}
                disabled={saving}
                className='px-3 py-1 text-xs rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50'
              >
                Guardar clientes
              </button>
            </div>
          </div>

          <div>
            <div className='flex items-center justify-between mb-2'>
              <h3 className='text-xs font-semibold uppercase text-slate-500'>Abogados</h3>
              {!adding && (
                <button type='button' onClick={() => setAdding(true)} className='text-xs text-blue-600 hover:text-blue-700'>
                  + Añadir abogado
                </button>
              )}
            </div>
            {adding && (
              <NewUserForm
                title='Nuevo abogado'
                companies={partnerCompanies}
                onSubmit={createLawyer}
                onCancel={() => setAdding(false)}
              />
            )}
            {lawyers.length === 0 && !adding ? (
              <p className='text-xs text-slate-500'>Este despacho todavía no tiene abogados.</p>
            ) : (
              <ul className='space-y-2 mt-2'>
                {lawyers.map(l => (
                  <LawyerRow key={l._id} lawyer={l} partnerCompanies={partnerCompanies} onSaved={loadLawyers} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export default PartnerCard;
